import type { ReactNode } from 'react'
import React from 'react'
import { useLocation } from 'react-router-dom'

import { DASHBOARD_STYLED_CONSTS, Dashboard } from '@components/common'
import { Tabs, useMediaQueryContext } from '@fourthwall/components'
import type { TabProps } from '@fourthwall/components/src/common/Tabs/Tab/types'
import { WebViewVisibilityDecorator } from '@fourthwall/webview-bridge'

import { SettingsMenuItem } from './SettingsMenuItem'
import type { SettingsMenuItemProps } from './SettingsMenuItem/types'
import * as S from './styled'

type IconComponent = (props: React.SVGAttributes<SVGElement> & { alt?: string }) => JSX.Element

export type SettingsTab = {
  name: string
  path: string
  icon: IconComponent
  activeIcon: IconComponent
  tag?: string
  items?: SettingsTab[]
  hidden?: boolean
}

export type SettingsTabsProps = {
  tabs: SettingsTab[]
  element: ReactNode
}

export const SettingsTabs = ({ tabs, element }: SettingsTabsProps) => {
  const { pathname } = useLocation()
  const media = useMediaQueryContext()
  const direction = media.tablet ? 'vertical' : 'horizontal'
  const mobileTabProps: Partial<TabProps> = !media.tablet
    ? {
        variant: 'dimmed',
        size: 'medium',
        weight: 'bold',
        scrollable: true,
        scrollIntoView: true,
        scrollIntoViewOffset: DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_HORIZONTAL,
      }
    : {}

  const findActiveTabIndex = () => {
    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i]
      if (tab.path === pathname || pathname.startsWith(`${tab.path}/`)) {
        return i
      }
      // Check nested items
      if (tab.items) {
        for (const item of tab.items) {
          if (item.path === pathname || pathname.startsWith(`${item.path}/`)) {
            return i
          }
        }
      }
    }

    return 0
  }

  const defaultIndex = findActiveTabIndex()

  const transformTabToMenuItem = (tab: SettingsTab): SettingsMenuItemProps => ({
    label: tab.name,
    to: tab.path,
    icon: tab.icon,
    activeIcon: tab.activeIcon,
    tag: tab.tag,
    items: tab.items?.filter((item) => !item.hidden).map(transformTabToMenuItem),
  })

  return (
    <>
      <WebViewVisibilityDecorator on="webview">
        <S.Container>{element}</S.Container>
      </WebViewVisibilityDecorator>
      <WebViewVisibilityDecorator on="web">
        <Tabs direction={direction} defaultIndex={defaultIndex}>
          <Dashboard.Section.ScrollableContent hideScrollbar fullWidth>
            <S.ListWrapper>
              {media.tablet ? (
                <S.TabsListWrapper direction={direction}>
                  {tabs.map((tab) => {
                    const menuItem = transformTabToMenuItem(tab)

                    return <SettingsMenuItem key={tab.name} {...menuItem} />
                  })}
                </S.TabsListWrapper>
              ) : (
                <Tabs.List direction={direction}>
                  {tabs.map(({ name, path }) => (
                    <S.Tab
                      key={name}
                      name={name}
                      to={path}
                      direction={direction}
                      size="large"
                      {...mobileTabProps}
                    />
                  ))}
                </Tabs.List>
              )}
            </S.ListWrapper>
          </Dashboard.Section.ScrollableContent>
          <S.Container>{element}</S.Container>
        </Tabs>
      </WebViewVisibilityDecorator>
    </>
  )
}
